import { useClerk, useUser } from '@clerk/clerk-react';
import type { UserRole } from '@vrplatform/log';
import { type Maybe, day } from '@vrplatform/utils';
import {
  useCallback,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ApiClientProvider } from '../../api';
import { isLocalhost } from '../../env';
import { useQuery } from '../../graphql';
import { useTrpcQuery } from '../../trpc';
import { OwnerPortalContactContextProvider } from '../owner';
import { useIntercom } from '../useIntercom';
import { useIsPlaywright } from '../usePlaywright';
import { useTrackingIdentify } from '../useTracking';
import {
  teamContext,
  teamIdContext,
  userContext,
  useTeamIdContext,
} from './team.context';
import {
  _useLocalStorageTeamId,
  useTeamApiQuery,
  useUserQuery,
} from './team.queries';
import {
  getRecentTeamIdsStorageKey,
  parseStoredRecentTeamIds,
  resolveSessionTeamFetchingState,
  resolveSessionTeamId,
  upsertRecentTeamId,
} from './team.session.utils';

function __useMe(args: {
  userId: string | undefined;
  realUserId: string | undefined;
  role: string | undefined;
}) {
  const { user: u } = useUser();
  const avatar = u?.imageUrl;
  const identify = useTrackingIdentify();
  const intercom = useIntercom();

  const {
    data: userData,
    isLoading: loadingUser,
    error: errorUser,
    refetch: refetchUser,
  } = useUserQuery(args);

  const {
    data: teamData,
    isLoading: loadingTeam,
    error: errorTeam,
    refetch: refetchTeam,
    isFetching: isFetchingTeam,
    queryKey: teamQueryKey,
  } = useTeamApiQuery(args);

  const user = userData?.user;
  const team = teamData?.team;

  const { data, isInitialLoading: loadingIntercomHash } = useTrpcQuery(
    'fetchIntercomHash',
    {
      tenantId: team?.id!,
    },
    {
      skip: !team?.id || !user?.id,
      refetchOnWindowFocus: false,
    }
  );

  const isTeamAdmin = useMemo(
    () =>
      user?.isSuperAdmin ||
      user?.isVrpAdmin ||
      user?.isPartnerAdmin ||
      team?.role === 'admin',
    [user?.isSuperAdmin, user?.isVrpAdmin, user?.isPartnerAdmin, team?.role]
  );

  const canAccessTeam = useMemo(
    () =>
      isTeamAdmin ||
      user?.isPartnerAdmin ||
      user?.isVrpAdmin ||
      user?.isSuperAdmin ||
      !!team?.membershipId,
    [
      isTeamAdmin,
      user?.isPartnerAdmin,
      user?.isVrpAdmin,
      user?.isSuperAdmin,
      team?.membershipId,
    ]
  );

  const intercomIdentifyId = data?.intercom_user_hash;

  const isPlaywright = useIsPlaywright(user?.email || '');

  const enableIdentify =
    !loadingIntercomHash &&
    !loadingUser &&
    !loadingTeam &&
    !!teamData &&
    !!userData?.user;

  useEffect(() => {
    if (isPlaywright || isLocalhost) {
      intercom.hardShutdown(); // prevent showing intercom completely
      return;
    }

    if (!enableIdentify || !user?.id || !intercomIdentifyId) return;

    identify({
      intercomIdentifyId,
      user: {
        id: user.id,
        email: user.email,
        avatar,
        lastSeenAt: day().toISOString(),
        name: user.name,
        firstName: user.firstName,
        lastName: user.lastName,
        createdAt: day(user.createdAt).toISOString(),
        isPartnerAdmin: user.isPartnerAdmin,
        isVrpAdmin: user.isVrpAdmin,
        isSuperAdmin: user.isSuperAdmin,
        isTeamAdmin,
        country: undefined, // TODO: add country?
        role: (user.isSuperAdmin
          ? 'super-admin'
          : user.isVrpAdmin
            ? 'vrp-admin'
            : user.isPartnerAdmin
              ? 'partner-admin'
              : isTeamAdmin
                ? 'team-admin'
                : team?.role ||
                  args.role || // this is the clerk role // (admin, user, owner)
                  'user') as UserRole,
      },
      team: team
        ? {
            id: team.id,
            name: team.name,
            isOnboarding: team.isOnboarding,
            isGL: team.isGL,
            type: team.type,
          }
        : undefined,
    });
  }, [
    enableIdentify,
    user,
    team,
    identify,
    isTeamAdmin,
    intercomIdentifyId,
    isPlaywright,
    avatar,
    args.role,
    intercom.hardShutdown,
  ]);

  return {
    user: user
      ? { ...user, realUserId: args.realUserId, canAccessTeam, isTeamAdmin }
      : undefined,
    team,
    loading: loadingUser || loadingTeam,
    error: errorUser || errorTeam,
    isFetchingTeam,
    teamQueryKey,
    refetch: async () => {
      await Promise.allSettled([refetchUser(), refetchTeam()]);
    },
  };
}

type SessionUserProviderProps = {
  children: ReactNode;
  isBootstrappingTeamId: boolean;
  isTeamSwitching: boolean;
  onTeamQueryFetchingChange: (value: boolean) => void;
  onTeamSwitchingChange: (value: boolean) => void;
};

const SessionUserProvider = ({
  children,
  isBootstrappingTeamId,
  isTeamSwitching,
  onTeamQueryFetchingChange,
  onTeamSwitchingChange,
}: SessionUserProviderProps) => {
  const { user: u } = useUser();
  const { teamId: selectedTeamId, setTeamId } = useTeamIdContext();

  // const { ownerPreview } = useOwnerPreviewId();
  const realUserId = `${u?.publicMetadata.user_id || ''}`;
  // const ownerPreviewUserId = ownerPreview?.userId?.trim() || '';
  // const ownerPreviewTeamId = ownerPreview?.teamId?.trim() || '';

  const userId = `${u?.publicMetadata.user_id || ''}` || undefined;

  const { user, refetch, team, isFetchingTeam, teamQueryKey, error } = __useMe({
    userId,
    role: (u?.publicMetadata.role as Maybe<'user' | 'owner' | 'admin'>)
      ? `${u?.publicMetadata.role}`
      : undefined,
    realUserId,
  });

  const isTeamQueryFetching = isFetchingTeam && !isBootstrappingTeamId;

  useEffect(() => {
    onTeamQueryFetchingChange(isTeamQueryFetching);
  }, [isTeamQueryFetching, onTeamQueryFetchingChange]);

  useEffect(() => {
    if (!isTeamSwitching) return;

    const resolvedTeam =
      selectedTeamId === null ||
      (selectedTeamId ? team?.id === selectedTeamId : false);

    if (!isFetchingTeam && (resolvedTeam || error)) {
      onTeamSwitchingChange(false);
    }
  }, [
    isTeamSwitching,
    isFetchingTeam,
    selectedTeamId,
    team?.id,
    error,
    onTeamSwitchingChange,
  ]);

  // For webextension
  useEffect(() => {
    if (u?.id) localStorage.setItem('cid', u.id);
    else localStorage.removeItem('cid');
    if (userId) localStorage.setItem('uid', userId);
    else localStorage.removeItem('uid');

    const tid = selectedTeamId || team?.id;
    if (tid) localStorage.setItem('tid', tid);
    else localStorage.removeItem('tid');
  }, [userId, u?.id, selectedTeamId, team?.id]);

  useEffect(() => {
    if (!userId || !team?.id) return;

    const storageKey = getRecentTeamIdsStorageKey(userId);
    const recentTeamIds = parseStoredRecentTeamIds(
      globalThis.localStorage?.getItem(storageKey) ?? null
    );
    const nextRecentTeamIds = upsertRecentTeamId({
      teamId: team.id,
      recentTeamIds,
    });

    globalThis.localStorage?.setItem(
      storageKey,
      JSON.stringify(nextRecentTeamIds)
    );
  }, [userId, team?.id]);

  const isTeamFetching =
    isBootstrappingTeamId || isFetchingTeam || isTeamSwitching;

  const teamValue = useMemo(() => {
    return {
      teamId: team?.id!,
      setTeamId,
      team: team! || {},
      role: team?.role as any,
      isSuperAdmin: user?.isSuperAdmin ?? false,
      isVrpAdmin: user?.isVrpAdmin ?? false,
      isPartnerAdmin: user?.isPartnerAdmin ?? false,
      isTeamAdmin: user?.isTeamAdmin ?? false,
      refetchTeam: refetch,
      isFetchingTeam: isTeamFetching,
      queryError: error,
      queryKey: teamQueryKey,
    };
  }, [
    team,
    setTeamId,
    refetch,
    user?.isSuperAdmin,
    user?.isVrpAdmin,
    user?.isPartnerAdmin,
    user?.isTeamAdmin,
    isTeamFetching,
    error,
    teamQueryKey,
  ]);

  return (
    <userContext.Provider value={user!}>
      <teamContext.Provider value={teamValue}>
        <OwnerPortalContactContextProvider>
          {children}
        </OwnerPortalContactContextProvider>
      </teamContext.Provider>
    </userContext.Provider>
  );
};

export function SessionProvider({ children }: { children: ReactNode }) {
  const { user: u } = useUser();
  const auth = useClerk();
  const userId = `${u?.publicMetadata.user_id || ''}` || undefined;
  const [teamId, setTeamId] = _useLocalStorageTeamId(userId);
  const [isTeamQueryFetching, setIsTeamQueryFetching] = useState(false);
  const [isTeamSwitching, setIsTeamSwitching] = useState(false);
  const shouldFetchTeamId =
    !!userId && (teamId === undefined || teamId === null);

  const { data: teamIdData, isLoading: isTeamIdLoading } = useQuery(
    (q) => {
      const id = q
        .tenant({
          limit: 1,
          order_by: [{ createdAt: 'desc' }],
        })
        .map((tenant) => tenant.id)[0];

      return {
        teamId: id,
      };
    },
    {
      skip: !shouldFetchTeamId,
      queryKey: ['teams'],
      teamScoped: false,
    }
  );

  const { resolvedTeamId, isBootstrappingTeamId } = resolveSessionTeamId({
    userId,
    teamId,
    fallbackTeamId: teamIdData?.teamId,
    isFallbackTeamIdLoading: isTeamIdLoading,
  });

  useEffect(() => {
    if (teamId === undefined && teamIdData?.teamId) {
      setTeamId(teamIdData.teamId);
    }
  }, [teamId, teamIdData?.teamId, setTeamId]);
  const { isApiTeamIdFetching } = resolveSessionTeamFetchingState({
    isBootstrappingTeamId,
    isTeamQueryFetching,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to add auth.session?.getToken to dependencies
  const getToken = useCallback(async () => {
    const token = await auth.session?.getToken({
      template: 'Hasura',
    });
    return token;
  }, [auth.session?.id]);

  const teamIdValue = useMemo(
    () => ({
      teamId: resolvedTeamId,
      setTeamId,
      isTeamSwitching,
      setIsTeamSwitching,
    }),
    [resolvedTeamId, setTeamId, isTeamSwitching, setIsTeamSwitching]
  );

  return (
    <teamIdContext.Provider value={teamIdValue}>
      <ApiClientProvider
        getToken={getToken}
        teamId={resolvedTeamId}
        isTeamIdFetching={isApiTeamIdFetching}
        isTeamSwitching={isTeamSwitching}
      >
        <SessionUserProvider
          isBootstrappingTeamId={isBootstrappingTeamId}
          isTeamSwitching={isTeamSwitching}
          onTeamQueryFetchingChange={setIsTeamQueryFetching}
          onTeamSwitchingChange={setIsTeamSwitching}
        >
          {children}
        </SessionUserProvider>
      </ApiClientProvider>
    </teamIdContext.Provider>
  );
}
